import React, { useState } from 'react';
import { Sparkles, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';

export const AIGuidanceCard = ({ analysis, archetypes, priorities, weeklyFocus, habits, onRegenerate, isRegenerating, scores, domainLabels, onDomainClick }) => {
  const { t, language } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const [domainsOpen, setDomainsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="glass-active rounded-2xl overflow-hidden"
      data-testid="ai-guidance-card"
    >
      {/* Header — always visible, clickable to toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-6 pb-4 text-left"
        data-testid="inner-guide-toggle"
      >
        <div className="flex items-center gap-3">
          <Sparkles className="text-[#D4AF37] w-5 h-5" />
          <h3 className="font-serif font-light text-xl text-[#F8F5F2]">
            {t('aiGuidance.title')}
          </h3>
        </div>
        <ChevronDown className={`w-5 h-5 text-[#6B7280] transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {/* Preview — archetypes always visible */}
      {!expanded && archetypes && archetypes.length > 0 && (
        <div className="px-6 pb-5 flex flex-wrap gap-2">
          {archetypes.map((archetype, index) => (
            <span
              key={index}
              className="font-serif text-sm text-[#F8F5F2] bg-[#D4AF37]/10 px-3 py-1 rounded-full border border-[#D4AF37]/30"
              data-testid={`archetype-${index}`}
            >
              {archetype}
            </span>
          ))}
          {weeklyFocus && (
            <p className="w-full text-[#A1A1AA] font-sans text-sm mt-2 line-clamp-1 italic">
              {weeklyFocus}
            </p>
          )}
        </div>
      )}

      {/* Expanded content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 space-y-6">
              {/* Regenerate button */}
              {onRegenerate && (
                <div className="flex justify-end">
                  <button
                    onClick={(e) => { e.stopPropagation(); onRegenerate(); }}
                    disabled={isRegenerating}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-all duration-300 font-sans text-xs disabled:opacity-50"
                    data-testid="regenerate-analysis-button"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isRegenerating ? 'animate-spin' : ''}`} />
                    <span className="hidden sm:inline">
                      {isRegenerating 
                        ? (language === 'tr' ? 'Çevriliyor...' : 'Translating...') 
                        : (language === 'tr' ? 'Türkçeye Çevir' : 'Translate')
                      }
                    </span>
                  </button>
                </div>
              )}

              {/* Explore 12 Life Domains — collapsible sub-button (anchored at bottom below) */}
              {/* (rendered after the Full Reading block) */}

              {/* Archetypes */}
              {archetypes && archetypes.length > 0 && (
                <div className="border-l-2 border-[#D4AF37]/50 pl-4" data-testid="archetypes-section">
                  <p className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold mb-2">
                    {t('aiGuidance.activeArchetypes')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {archetypes.map((archetype, index) => (
                      <span
                        key={index}
                        className="font-serif text-sm text-[#F8F5F2] bg-[#D4AF37]/10 px-3 py-1 rounded-full border border-[#D4AF37]/30"
                      >
                        {archetype}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Weekly Focus */}
              {weeklyFocus && (
                <div className="border-l-2 border-[#D4AF37] pl-4" data-testid="weekly-focus-section">
                  <p className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold mb-2">
                    {t('aiGuidance.weeklyFocus')}
                  </p>
                  <p className="font-serif text-lg text-[#F8F5F2] font-light italic">
                    {weeklyFocus}
                  </p>
                </div>
              )}

              {/* Priorities */}
              {priorities && priorities.length > 0 && (
                <div data-testid="priorities-section">
                  <p className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold mb-3">
                    {t('aiGuidance.priorities')}
                  </p>
                  <ul className="space-y-2">
                    {priorities.map((priority, index) => (
                      <li key={index} className="font-sans font-light text-[#D1D1D6] flex items-start gap-2" data-testid={`priority-${index}`}>
                        <span className="text-[#D4AF37] mt-1">&#8226;</span>
                        <span>{priority}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Habits */}
              {habits && habits.length > 0 && (
                <div data-testid="habits-section">
                  <p className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold mb-3">
                    {t('aiGuidance.weeklyHabits')}
                  </p>
                  <ul className="space-y-2">
                    {habits.map((habit, index) => (
                      <li key={index} className="font-sans font-light text-[#D1D1D6] flex items-start gap-2" data-testid={`habit-${index}`}>
                        <span className="text-[#D4AF37] mt-1">&#8226;</span>
                        <span>{habit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Analysis text — moved BELOW the graphics so the long read sits at the bottom */}
              <div data-testid="ai-analysis-text" className="pt-2 border-t border-white/5">
                <p className="font-sans uppercase tracking-[0.2em] text-[#D4AF37] text-xs font-semibold mb-3 mt-4">
                  {language === 'tr' ? 'Tam Okuma' : 'Full Reading'}
                </p>
                <p className="font-sans font-light leading-relaxed text-[#D1D1D6] whitespace-pre-wrap">
                  {analysis}
                </p>
              </div>

              {/* Explore 12 Life Domains — its OWN collapsible button at the bottom */}
              {scores && Object.keys(scores).length > 0 && domainLabels && (
                <div data-testid="explore-domains-section" className="pt-2">
                  <button
                    onClick={() => setDomainsOpen(!domainsOpen)}
                    className="w-full flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#D4AF37]/30 transition-all"
                    data-testid="toggle-domains"
                  >
                    <span className="font-sans text-sm text-[#F8F5F2] uppercase tracking-[0.2em]">
                      {language === 'tr' ? '12 Yaşam Alanını Keşfet' : 'Explore 12 Life Domains'}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-[#A1A1AA] transition-transform duration-300 ${domainsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {domainsOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 mt-3">
                          {Object.entries(scores).map(([key, score]) => (
                            <button
                              key={key}
                              onClick={() => onDomainClick && onDomainClick(key)}
                              className="p-3 rounded-xl bg-black/40 border border-white/10 hover:border-[#D4AF37]/40 transition-all text-left group"
                              data-testid={`inner-guide-domain-${key}`}
                            >
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-xl font-serif text-[#D4AF37]">{score}</span>
                                <ChevronRight className="w-3.5 h-3.5 text-[#6B7280] group-hover:text-[#D4AF37] transition-colors" />
                              </div>
                              <p className="text-[10px] text-[#A1A1AA] font-sans uppercase tracking-wider leading-tight">
                                {domainLabels[key]}
                              </p>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AIGuidanceCard;
